import React, { useState, useEffect } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/system";
import { Menu } from "@mui/material";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import DataTable from "Tables/DataTable";

// sweetalert2 components
import Swal from "sweetalert2";

import { getData, postData } from "Utils/server";
import { getAuthorizationHeader } from "Utils/helpers";
import SoftSelect from "components/SoftSelect";

// react-router components
import { useNavigate } from "react-router-dom";

import { useSoftUIController } from "context";

/**
 *
 */
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    //backgroundColor: "#f5f5f9",
    backgroundColor: "#000",
    color: "rgba(f, f, f, 1)", //"rgba(0, 0, 0, 0.87)",
    placement: "left",
    maxWidth: 600,
    minWidth: "minContent",
    height: "minContent",
    textAlign: "left",
    fontSize: "0.65erm",
    border: "1px solid #dadde9",
    boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
    padding: "15px",
    marginLeft: "15px",
  },
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="outlined" {...props} />;
});

let settlementOutcomes = [];
let availableProviders = [];
let settlements = [];
let fixtureData = null;
let dataRows = [];
function EditSettlement({ showModal, signalCloseModal, data }) {
  const [controller, dispatch] = useSoftUIController();
  const { sports, providers } = controller;

  const [open, setOpen] = useState(false);
  const [selectedFixture, setSelectedFixture] = useState(data || null);
  const [updateEnabled, setUpdateEnabled] = useState(false);
  const [notification, setNotification] = useState(false);
  const [apiResponse, setApiresponse] = useState("success");
  const [apiResponseMsg, setApiResponseMsg] = useState("success");
  const [loading, setLoading] = useState(true);
  const spSportsAvailable = ["greyhounds", "trotting", "horseracing"];
  const [hiddenColumns, setHiddenColumns] = useState(["tm"]);
  const [noteExist, setNoteExist] = useState(false);
  const [openActionsMenu, setOpenActionsMenu] = useState(false);

  const [dataTableData, setDataTableData] = useState({
    columns: [
      {
        Header: "timestamp",
        accessor: "tm",
        width: "100px",
      },
      {
        Header: "UUID",
        accessor: "uuid",
        width: "100px",
        Cell: formatUuid,
      },
      {
        Header: "Market Name",
        accessor: "name",
        width: "100px",
        Cell: formatCell,
      },
      // { Header: "Market Numbers", accessor: "numbers", width: "100px", Cell: mergeArray },
      { Header: "odd.e", accessor: "e", width: "100px", Cell: renderProperties },
      { Header: "odd.h", accessor: "h", width: "100px", Cell: renderProperties },
      { Header: "odd.o", accessor: "o", width: "100px", Cell: renderProperties },
      { Header: "odd_name", accessor: "odd_name", width: "100px", Cell: renderCell },
      { Header: "SP", accessor: "sp", width: "120px", Cell: renderSP },
      { Header: "Payment %", accessor: "payment_percentage", width: "120px", Cell: renderPayment },
      { Header: "action", accessor: "action", width: "120px", Cell: possibleSettlement },
      { Header: "votes", accessor: "votes", width: "120px", Cell: renderVotes },
      { Header: "note", accessor: "note", width: "120px", Cell: editNotes },
    ],

    rows: dataRows,
  });

  const navigate = useNavigate();

  
  const sleep = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  function openAdvancedSearch(e, data) {
    e.preventDefault();

    window.open("/settlement/advanced?" + data[0] + "=" + data[1]);
  }

  async function fetchOdds(fixtureIds, sportName) {
    setLoading(true);
    await setDataTableData({ columns: [], rows: [] });
    setHiddenColumns([]);
    let sport = sports.find((item) => item.name === sportName);
    if (!sport || !fixtureIds) {
      setLoading(false);
      return;
    }
    let getOddsEndpoint = `/api/v1/bets/unsettled/fixtures/${sport.id}/ids`;
    let response = await postData(getOddsEndpoint, fixtureIds, {
      Authorization: getAuthorizationHeader(),
    });
    if (response != null) {
      //console.log(response.data);
      await sleep(50);
      let dataTable = { ...dataTableData };
      dataTable.rows = response.data.map((odd) => {
        if (odd.note) setNoteExist(true);
        odd.tm = new Date().getTime();
        switch (odd.market_type) {
          case 1:
            odd.fast_market = true;
            odd.players_market = false;
            break;
          case 2:
            odd.fast_market = false;
            odd.players_market = true;
            break;
          case 3:
            odd.fast_market = true;
            odd.players_market = true;
            break;
          default:
            odd.fast_market = false;
            odd.players_market = false;
            break;
        }
        return odd;
      });
      setDataTableData(dataTable);
      dataRows = response.data;
    }
    if (!spSportsAvailable.includes(fixtureData.sport.toLowerCase().replace(/ /g, ""))) {
      setHiddenColumns(["tm", "sp"]);
    } else {
      setHiddenColumns(["tm"]);
    }

    setLoading(false);
  }

  async function fetchSettlementValues() {
    if (settlementOutcomes.length > 0) return;
    let settlementOutcomesEndPoint = `/api/v1/entities/availableSettlementOutcomes`;
    let response = await getData(settlementOutcomesEndPoint, {
      Authorization: getAuthorizationHeader(),
    });
    if (response != null) {
      //console.log(response.data);
      const noneOption = [{ value: 0, label: "Select..." }];
      const apiOptions = response.data.map((item) => {
        return { value: item.id, label: item.description };
      });

      const allOptions = noneOption.concat(apiOptions);

      settlementOutcomes = allOptions.map((item) => {
        return { value: item.value, label: item.label };
      });
    }
  }

  function renderCell({ value, ...rest }) {
    return <div className="cell-width">{value}</div>;
  }

  function formatCell({ value, ...rest }) {
    return (
      <div className="cell-width">
        <HtmlTooltip
          placement="right"
          title={
            <React.Fragment>
              <div style={{ display: "flex", flexWrap: "wrap" }}>{rest.row.original.uni_name}</div>
            </React.Fragment>
          }
        >
          <div>
            <div>{value}</div>
            <Icon
              style={{
                display: rest.row.original.fast_market ? "" : "none",
                color: "forestgreen",
              }}
            >
              bolt_outlined_icon
            </Icon>
            <Icon
              style={{
                display: rest.row.original.players_market ? "" : "none",
                color: "forestgreen",
              }}
            >
              directions_run_outlined_icon
            </Icon>
          </div>
        </HtmlTooltip>
      </div>
    );
  }

  function formatUuid({ value, ...rest }) {
    return (
      <div style={{ cursor: "pointer" }}>
        <span className="info-icon" onClick={(e) => openAdvancedSearch(e, ["odd_uuid", value])}>
          {value.toString().substring(1, 10) + "..."}
        </span>
        <Icon className="info-icon" onClick={(e) => handleUUIDCopy(e, value)}>
          copy
        </Icon>
      </div>
    );
  }

  /**
   *
   */
  const handleUUIDCopy = (e, UUID) => {
    //console.log("Copy ApiKey: ", apikey);
    copyToClipboard(UUID);
  };

  const copyToClipboard = (textToCopy) => {
    setApiresponse("success");
    setApiResponseMsg("Value has been copied to the clipboard!");
    setNotification(true);
    // navigator clipboard api needs a secure context (https)
    if (navigator.clipboard && window.isSecureContext) {
      // navigator clipboard api method'
      //console.log("window.isSecureContext");
      return navigator.clipboard.writeText(textToCopy);
    } else {
      // console.log("text area method");
      // text area method
      let textArea = document.createElement("textarea");
      textArea.value = textToCopy;
      // make the textarea out of viewport
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      return new Promise((res, rej) => {
        // here the magic happens
        document.execCommand("copy") ? res() : rej();
        textArea.remove();
      });
    }
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setNotification(false);
  };

  const handleCancelEdit = () => {
    if (settlements.length > 0) {
      Swal.fire({
        title: `Unsaved Data!`,
        html: `You are going Lose some Odds you have already settle.</br></br> <SoftBox pr={1} style={{ marginRight: "auto" }}>
            <SoftTypography variant="caption">${settlements.length}</SoftTypography>
            <SoftTypography variant="caption"> - </SoftTypography>
            <SoftTypography variant="caption" style="color:#cb0c9f">
              odds Settlements
            </SoftTypography>
          </SoftBox>
          </br></br>
          Are you Sure?`,
        showDenyButton: true,
        showConfirmButton: true,
        confirmButtonText: "Yes, Cancel them!",
        denyButtonText: "Cancel",
        icon: "warning",
        confirmButtonColor: "#87cb16",
        denyButtonColor: "#ff4a55",
      }).then(async (result) => {
        if (result.isConfirmed) {
          setOpen(false);
          signalCloseModal(true, null);
        }
      });
    } else {
      setOpen(false);
      signalCloseModal(true, null);
    }
  };

  const handleUpdate = () => {
    Swal.fire({
      title: `You are going to Settle a number of Odds. \nAre you Sure?`,
      html: `<SoftBox pr={1} style={{ marginRight: "auto" }}>
            <SoftTypography variant="caption">${settlements.length}</SoftTypography>
            <SoftTypography variant="caption"> - </SoftTypography>
            <SoftTypography variant="caption" style="color:#cb0c9f">
              odds Settlements
            </SoftTypography>
          </SoftBox>`,
      showDenyButton: true,
      showConfirmButton: true,
      confirmButtonText: "Yes, Settle them!",
      denyButtonText: "Cancel",
      icon: "warning",
      confirmButtonColor: "success",
      // denyButtonColor: "#ff4a55",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const settleOddEndPoint = `/api/v1/bets/settleOdd`;
        const settlementOutcomesVar = getSettlements();
        const response = await postData(
          settleOddEndPoint,
          { signal: "evaluation", values: settlementOutcomesVar },
          {
            Authorization: getAuthorizationHeader(),
          }
        );

        if (response != null) {
          Swal.fire("Request Sent!", `Odds Settlements Sent to system.`, "success");
          setOpen(false);
          signalCloseModal(true, null);
        } else {
          Swal.fire("Error!", `Something went wrong.`, "error");
          setOpen(false);
          signalCloseModal(true, null);
        }
      }
    });
  };

  function formatDateStringUTCtoLocale(value) {
    if (!value) return "-";
    let date = new Date(value);
    let day = date.getDate() < 10 ? "0" + date.getDate() : date.getDate();
    let month = date.getMonth() + 1 < 10 ? "0" + (date.getMonth() + 1) : date.getMonth() + 1;
    let hours = date.getHours() < 10 ? "0" + date.getHours() : date.getHours();
    let minutes = date.getMinutes() < 10 ? "0" + date.getMinutes() : date.getMinutes();
    let secs = date.getSeconds() < 10 ? "0" + date.getSeconds() : date.getSeconds();
    let milisecs =
      date.getMilliseconds() < 100
        ? "0" + date.getMilliseconds()
        : date.getMilliseconds() < 10
        ? "00" + date.getMilliseconds()
        : date.getMilliseconds();

    let dateformatted =
      day +
      "/" +
      month +
      "/" +
      date.getFullYear() +
      " " +
      hours +
      ":" +
      minutes +
      ":" +
      secs +
      "." +
      milisecs;
    return dateformatted;
  }

  const handleBlockFixture = async (e, fixture) => {
    e.preventDefault();

        let fixtures = [];

    if (fixture.providerId == 1000) {
      fixture.providerInfo.map((f) => {
        let provider = availableProviders.find((p) => p.info === f.provider);
        fixtures.push({
          provider_id: provider ? provider.value : 1000,
          fixture_id: f.pid,
          status: !fixture.blocked,
        });
      });
      fixtures.push({
        provider_id: fixture.providerId,
        fixture_id: fixture.fixture_id,
        status: !fixture.blocked,
      });
    } else {
      fixtures.push({
        provider_id: fixture.providerId,
        fixture_id: fixture.fixture_id,
        status: !fixture.blocked,
      });
    }

    Swal.fire({
      title: `You are going to ${fixture.blocked ? "UN" : ""}BAN ${
        fixtures.length > 1 ? "these Fixtures" : "this Fixture"
      } from Feed. \nAre you Sure?`,
      html: `<SoftBox pr={1} style={{ marginRight: "auto" }}>
                ${fixtures.map(
                  (f) =>
                    `<SoftTypography key=${f.fixture_id} variant="caption">
                    ${f.fixture_id}
                  </SoftTypography>`
                )}
                
              </SoftBox>`,
      showDenyButton: true,
      showConfirmButton: true,
      confirmButtonText: `Yes, ${fixture.blocked ? "UN" : ""}BAN ${
        fixtures.length == 1 ? "it" : "them"
      }!`,
      denyButtonText: "Cancel",
      icon: "warning",
      confirmButtonColor: "success",
      // denyButtonColor: "#ff4a55",
    }).then(async (result) => {
      if (result.isConfirmed) {
        let setOddNoteEndPoint = `/api/v2/panel/filters/fixtures/blocked`;

        let response = await postData(setOddNoteEndPoint, fixtures, {
          Authorization: getAuthorizationHeader(),
        });

        if (response != null) {
          setApiresponse("success");
          setApiResponseMsg("Fixture Blocked succesfully!");
          fixture.blocked = !fixture.blocked;
          setNotification(true);
        } else {
          setApiresponse("error");
          setApiResponseMsg(
            "Something went wrong! Fixture doesn't Blocked. Please try again later. "
          );
          setNotification(true);
        }
      }
    });
  };

  const postponeEvent = async (e) => {
    e.preventDefault();
    setApiresponse("info");
    setApiResponseMsg("Not Implemented yet!");
    setNotification(true);
    handleCloseActionsMenu();
  };

  const refundAllUnsettledOdds = async (e) => {
    e.preventDefault();
    await settleAllVisibleOdds(3);
    handleCloseActionsMenu();
  };

  const unsettledAllOdds = async (e) => {
    e.preventDefault();
    await settleAllVisibleOdds(6);
    handleCloseActionsMenu();
  };

  const settleAllVisibleOdds = async (settlement_id) => {
    setUpdateEnabled(false);
    setHiddenColumns([]);
    await setDataTableData({ columns: [], rows: [] });

    let rows = [...dataRows];
    let dataTable = { ...dataTableData };

    for (let index = 0; index < rows.length; index++) {
      const row = rows[index];
      row.tm = new Date().getTime();
      row.outcome_id = settlement_id;
      row.unconfirmed_outcome_id = row.unconfirmed_outcome_id
        ? settlement_id
        : row.unconfirmed_outcome_id;
      const settlement = {
        uuid: row.uuid,
        outcome_id: row.outcome_id,
        event_id: fixtureData.fixture_id,
        provider_id: fixtureData.providerId,
        sp: row.sp,
        payment_percentage: row.payment_percentage,
      };
      const settlementExist = settlements.find((item) => item.uuid === settlement.uuid);
      if (!settlementExist) {
        if (settlement.outcome_id === 0) return;
        settlements.push(settlement);
      } else {
        if (settlement.outcome_id === 0) {
          let settlementExistIndex = settlements.findIndex(
            (item) => item.uuid === settlementExist.uuid
          );
          settlements.splice(settlementExistIndex, 1);
        } else {
          settlementExist.outcome_id = settlement.outcome_id;
        }
      }
    }

    dataTable.rows = dataRows;
    setDataTableData(dataTable);
    setHiddenColumns(["tm", "sp"]);
    dataRows = rows;

    if (settlements.length > 0) setUpdateEnabled(true);
  };

  const handleSettlementOutcomeChange = async (item, row) => {
    setUpdateEnabled(false);
    const settlement = {
      uuid: row.original.uuid,
      outcome_id: item.value,
      event_id: fixtureData.fixture_id,
      provider_id: fixtureData.providerId,
      sp: row.original.sp,
      payment_percentage: row.original.payment_percentage,
    };

    let rows = [...dataRows];
    let uuidFound = rows.find((r) => r.uuid == row.original.uuid);
    uuidFound.outcome_id = item.value;
    uuidFound.unconfirmed_outcome_id = uuidFound.unconfirmed_outcome_id
      ? item.value
      : uuidFound.unconfirmed_outcome_id;

    let dataTable = { ...dataTableData };
    dataTable.rows = dataRows;
    setDataTableData(dataTable);

    dataRows = rows;

    const settlementExist = settlements.find((item) => item.uuid === settlement.uuid);
    if (!settlementExist) {
      if (settlement.outcome_id === 0) return;
      settlements.push(settlement);
    } else {
      if (settlement.outcome_id === 0) {
        let settlementExistIndex = settlements.findIndex(
          (item) => item.uuid === settlementExist.uuid
        );
        settlements.splice(settlementExistIndex, 1);
      } else {
        settlementExist.outcome_id = settlement.outcome_id;
      }
    }
    if (settlements.length > 0) setUpdateEnabled(true);
  };

  const getSettlements = () => {
    return settlements;
  };

  function possibleSettlement({ value, ...rest }) {
    let outcome_id = rest.row.original.unconfirmed_outcome_id
      ? rest.row.original.unconfirmed_outcome_id
      : rest.row.original.outcome_id;
    let offlineSettlement = rest.row.original.settled_by_stats;
    let onlineSettlement = rest.row.original.settled;
    let already_settled = rest.row.original.already_settled;
    return rest.row.original.unconfirmed_outcome_id ? (
      <div style={{ display: "flex" }}>
        <div>
          <SoftSelect
            // sx={{ maxWidth: "200px" }}
            className={already_settled ? "already-settled" : "not-settled"}
            customSmall={true}
            options={settlementOutcomes}
            defaultValue={settlementOutcomes.find((item) => item.value === outcome_id)}
            onChange={(value) => handleSettlementOutcomeChange(value, rest.row)}
          ></SoftSelect>
        </div>
        <HtmlTooltip
          placement="right"
          title={
            <React.Fragment>
              <div style={{ display: "flex", flexWrap: "wrap" }}>Waiting for Confirmation!</div>
            </React.Fragment>
          }
        >
          <Button style={{ margin: "auto", padding: "2px" }}>
            <Icon
              style={{ color: "orange" }}
              onClick={(e) => openInNewWindow(e, "/settlement/confirmations")}
            >
              security_icon
            </Icon>
          </Button>
        </HtmlTooltip>

        <HtmlTooltip
          placement="top"
          title={
            <React.Fragment>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  color: already_settled ? "orange" : "#87cb16",
                  fontSize: "1.3rem !important",
                }}
              >
                Already settled: Inform your Supervisor.
              </div>
            </React.Fragment>
          }
        >
          <Icon
            style={{
              color: already_settled ? "red" : "#87cb16",
              display: already_settled ? "" : "none",
              marginTop: "auto",
              marginBottom: "auto",
              marginLeft: "10px",
              cursor: "help",
            }}
          >
            security_icon
          </Icon>
        </HtmlTooltip>
      </div>
    ) : (
      <div style={{ display: "flex" }}>
        <div>
          <SoftSelect
            // sx={{ maxWidth: "200px" }}
            className={already_settled ? "already-settled" : "not-settled"}
            customSmall={true}
            options={settlementOutcomes}
            defaultValue={settlementOutcomes.find((item) => item.value === outcome_id)}
            onChange={(value) => handleSettlementOutcomeChange(value, rest.row)}
          ></SoftSelect>
        </div>
        <HtmlTooltip
          placement="top"
          title={
            <React.Fragment>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  color: already_settled ? "orange" : "#87cb16",
                  fontSize: "1.3rem !important",
                }}
              >
                Already settled: Inform your Supervisor.
              </div>
            </React.Fragment>
          }
        >
          <Icon
            style={{
              color: already_settled ? "orange" : "#87cb16",
              display: already_settled ? "" : "none",
              marginTop: "auto",
              marginBottom: "auto",
              marginLeft: "10px",
              cursor: "help",
            }}
          >
            warning_amber_icon
          </Icon>
        </HtmlTooltip>
      </div>
    );
  }

  function renderVotes({ value, ...rest }) {
    let listItems = rest.row.original.votes; //.split(",");
    let listItemsDiscarded = rest.row.original.votes?.filter((v) => v.discarded == true);
    let listItemsNormal = rest.row.original.votes?.filter((v) => !v.discarded);

    return (
      <>
        <HtmlTooltip
          placement="right"
          title={
            <React.Fragment>
              {!listItems || listItems.length == 0
                ? "No Votes Exist."
                : listItems?.map((item, index) => {
                    let provider = availableProviders.find((p) => p.value === item.providerId);
                    let providerName = provider ? provider["info"] : item.providerId;
                    let voteDate = new Date(item.timestamp);
                    return (
                      <div
                        key={index}
                        style={{
                          opacity: item.discarded ? "60%" : "100",
                          color: item.discarded ? "red" : "",
                          width: "max-content",
                          display: "inline-flex",
                        }}
                      >
                        <div className={"provider-" + providerName + "-inverse provider-inverse"}>
                          {providerName}
                        </div>
                        <div>
                          {": "}
                          <span style={{ display: "inline-block", minWidth: "100px" }}>
                            {settlementOutcomes.find((s) => s.value == item.outcomeId).label}
                          </span>
                          {item.originId == 1 ? (
                            <span
                              style={{
                                color: "#66d432",
                                display: "inline-block",
                                minWidth: "150px",
                              }}
                            >
                              Online Settlement
                            </span>
                          ) : item.originId == 2 ? (
                            <span
                              style={{
                                color: "#d432bb",
                                display: "inline-block",
                                minWidth: "150px",
                              }}
                            >
                              Offline Settlement
                            </span>
                          ) : item.originId == 4 ? (
                            <span
                              style={{
                                color: "#f9d900",
                                display: "inline-block",
                                minWidth: "150px",
                              }}
                            >
                              FastTrack
                            </span>
                          ) : (
                            <span
                              style={{
                                color: "#3acaeb",
                                display: "inline-block",
                                minWidth: "150px",
                              }}
                            >
                              Operator
                            </span>
                          )}{" "}
                          ({formatDateStringUTCtoLocale(item.timestamp)})
                        </div>
                      </div>
                    );
                  })}
            </React.Fragment>
          }
        >
          <Button
            style={{
              padding: 0,
              padding: "0px",
              width: "30px",
              maxWidth: "30px",
              minWidth: "10px",
              color: listItems && listItems.length >= 0 ? "" : "red",
            }}
            onClick={(e) => getVotes(e, rest.row.original.uuid)}
          >
            <span style={{ paddingLeft: "5px" }}> </span>
            <Icon>how_to_vote_icon </Icon>
          </Button>
        </HtmlTooltip>
      </>
    );
  }

  async function getVotes(e, uuid) {
    let getOddVotesEndPoint = `/api/v1/settlements/votes`;
    let response = await postData(
      getOddVotesEndPoint,
      { uuid: uuid },
      {
        Authorization: getAuthorizationHeader(),
      }
    );

    if (response != null) {
      let dataTable = { ...dataTableData };

      // await setDataTableData({ columns: [], rows: [] });
      dataTable.rows = dataRows;

      for (let index = 0; index < dataTable.rows.length; index++) {
        const odd = dataTable.rows[index];
        if (odd.uuid === uuid) {
          odd.tm = new Date().getTime();
          odd.votes = response.data[uuid]?.sort((a, b) => b.timestamp - a.timestamp);
          break;
        }
      }
      setDataTableData(dataTable);
      await sleep(10);

      dataRows = dataTable.rows;

      setLoading(false);
    }
  }

  function editNotes({ value, ...rest }) {
    return (
      <div style={{ display: "grid" }}>
        <TextField
          className={"multilineTextField"}
          id="outlined-multiline-flexible"
          label="Notes"
          multiline
          maxRows={4}
          defaultValue={value}
          onChange={(e) => setNoteData(e, rest.row)}
        />
        <Button onClick={(e) => sentNotes(e, rest.row.original.note_id)}>sync Notes</Button>
      </div>
    );
  }

  function setNoteData(e, row) {
    e.preventDefault();

    row.original.note = e.currentTarget.value;
  }

  function renderProperties({ value, ...rest }) {
    return (
      <div style={{ color: value == "over" ? "green" : value == "under" ? "red" : "" }}>
        {value}
      </div>
    );
  }

  function renderSP({ value, ...rest }) {
    return (
      <div style={{ display: "flex" }} key={rest.row.original.uuid + "-" + rest.row.original.tm}>
        <SoftInput
          className="sp-value"
          placeholder="SP..."
          defaultValue={value}
          onChange={(e) => setSP(e, rest.row)}
        />
        <Button onClick={(e) => getSP(e, rest.row)}>
          <Icon
            style={{
              color: "#87cb16",
              marginTop: "auto",
              marginBottom: "auto",
              marginLeft: "10px",
            }}
          >
            download_for_offline_icon
          </Icon>
        </Button>
      </div>
    );
  }

  function renderPayment({ value, ...rest }) {
    return (
      <div
        style={{ display: "flex" }}
        key={rest.row.original.uuid + "-payment-" + rest.row.original.tm}
      >
        <SoftInput
          className="sp-value"
          placeholder="1%"
          defaultValue={value}
          onChange={(e) => setPayment(e, rest.row)}
        />
        <Button onClick={(e) => getPayment(e, rest.row)}>
          <Icon
            style={{
              color: "#87cb16",
              marginTop: "auto",
              marginBottom: "auto",
              marginLeft: "10px",
            }}
          >
            download_for_offline_icon
          </Icon>
        </Button>
      </div>
    );
  }

  async function sentNotes(e, note_id) {
    e.preventDefault();

    let noteToSend = dataRows.find((item) => item.note_id == note_id);

    let setOddNoteEndPoint = `/api/v1/bets/odd/note`;
    let response = await postData(
      setOddNoteEndPoint,
      { id: noteToSend.note_id, note: noteToSend.note },
      {
        Authorization: getAuthorizationHeader(),
      }
    );

    if (response != null) {
      setApiresponse("success");
      setApiResponseMsg("Note succesfully updated!");
      setNotification(true);
    } else {
      setApiresponse("error");
      setApiResponseMsg("Something went wrong! Note doesn't updated. Please try again later. ");
      setNotification(true);
    }

    // console.log(noteToSend);
  }

  async function getSP(e, row) {
    e.preventDefault();
    setLoading(true);
    let getOddSpEndPoint = `/api/v1/bets/odd/sp`;
    let uuid = row.original.uuid;
    let response = await postData(
      getOddSpEndPoint,
      { uuid: uuid, confirmed: row.original.unconfirmed_outcome_id ? false : true },
      {
        Authorization: getAuthorizationHeader(),
      }
    );
    if (response != null) {
      let dataTable = { ...dataTableData };

      // await setDataTableData({ columns: [], rows: [] });
      dataTable.rows = dataRows;

      for (let index = 0; index < dataTable.rows.length; index++) {
        const odd = dataTable.rows[index];
        if (odd.uuid === uuid) {
          odd.tm = new Date().getTime();
          odd.sp = response.data.sp;
          odd.sp_history = response.data.sps;
          break;
        }
      }
      setDataTableData(dataTable);
      await sleep(10);

      dataRows = dataTable.rows;

      setLoading(false);
    }
  }

  function setSP(e, row) {
    e.preventDefault();
    // console.log(e.currentTarget.value);
    // console.log(spData);
    setUpdateEnabled(false);
    const settlement = {
      uuid: row.original.uuid,
      outcome_id: row.original.outcome_id,
      event_id: fixtureData.fixture_id,
      provider_id: fixtureData.providerId,
      sp: e.currentTarget.value,
      payment_percentage: row.original.payment_percentage,
    };

    let rows = [...dataRows];
    let uuidFound = rows.find((r) => r.uuid == row.original.uuid);
    uuidFound.sp = e.currentTarget.value;

    let dataTable = { ...dataTableData };
    dataTable.rows = dataRows;
    setDataTableData(dataTable);

    dataRows = rows;

    const settlementExist = settlements.find((item) => item.uuid === settlement.uuid);
    if (!settlementExist) {
      if (settlement.outcome_id === 0) return;
      settlements.push(settlement);
    } else {
      if (settlement.outcome_id === 0) {
        let settlementExistIndex = settlements.findIndex(
          (item) => item.uuid === settlementExist.uuid
        );
        settlements.splice(settlementExistIndex, 1);
      } else {
        settlementExist.sp = settlement.sp;
      }
    }
    if (settlements.length > 0) setUpdateEnabled(true);
  }

  async function getPayment(e, row) {
    e.preventDefault();
    setLoading(true);
    let getOddSpEndPoint = `/api/v1/bets/odd/payment`;
    let uuid = row.original.uuid;
    let response = await postData(
      getOddSpEndPoint,
      { uuid: uuid, confirmed: row.original.unconfirmed_outcome_id ? false : true },
      {
        Authorization: getAuthorizationHeader(),
      }
    );
    if (response != null) {
      let dataTable = { ...dataTableData };

      // await setDataTableData({ columns: [], rows: [] });
      dataTable.rows = dataRows;

      for (let index = 0; index < dataTable.rows.length; index++) {
        const odd = dataTable.rows[index];
        if (odd.uuid === uuid) {
          odd.tm = new Date().getTime();
          odd.payment_percentage = response.data.payment;
          odd.payment_history = response.data.payments;
          break;
        }
      }
      setDataTableData(dataTable);
      await sleep(10);

      dataRows = dataTable.rows;

      setLoading(false);
    }
  }

  function setPayment(e, row) {
    e.preventDefault();
    // console.log(e.currentTarget.value);
    // console.log(spData);
    setUpdateEnabled(false);
    const settlement = {
      uuid: row.original.uuid,
      outcome_id: row.original.outcome_id,
      event_id: fixtureData.fixture_id,
      provider_id: fixtureData.providerId,
      sp: row.original.sp,
      payment_percentage: e.currentTarget.value,
    };

    let rows = [...dataRows];
    let uuidFound = rows.find((r) => r.uuid == row.original.uuid);
    uuidFound.payment_percentage = e.currentTarget.value;

    let dataTable = { ...dataTableData };
    dataTable.rows = dataRows;
    setDataTableData(dataTable);

    dataRows = rows;

    const settlementExist = settlements.find((item) => item.uuid === settlement.uuid);
    if (!settlementExist) {
      if (settlement.outcome_id === 0) return;
      settlements.push(settlement);
    } else {
      if (settlement.outcome_id === 0) {
        let settlementExistIndex = settlements.findIndex(
          (item) => item.uuid === settlementExist.uuid
        );
        settlements.splice(settlementExistIndex, 1);
      } else {
        settlementExist.payment_percentage = settlement.payment_percentage;
      }
    }
    if (settlements.length > 0) setUpdateEnabled(true);
  }

  function searchGoogle(e, verd) {
    e.preventDefault();
    var q = verd;
    window.open("http://google.com/search?q=" + q);
  }

  function searchBetsApi(e, verd) {
    e.preventDefault();
    var q = verd;
    window.open("https://betsapi.com/search/?q=" + q.split(" vs ")[0]);
  }

  function searchVbet(e, id) {
    e.preventDefault();
    window.open("https://sport.vbet.am/en/sports/pre-match/results?game=" + id);
  }

  function searchEfBet(e, id) {
    e.preventDefault();
    window.open("https://www.efbet.gr/en/sports/pre-match/results?game=" + id);
  }

  function searchBetfoot(e, id, sport) {
    e.preventDefault();
    if (id && sport) window.open(`https://betfoot.com/live/${sport}/1/${id}`);
  }

  function searchSofa(e, fixture) {
    e.preventDefault();
    let sport = fixture.sport.toLowerCase();
    let home = fixture.home;
    let url = `https://duckduckgo.com/?q=!ducky+inurl:sofascore.com/team/${sport}  ${home}`;
    window.open(url.toString(), "_blank", "noreferrer");
  }

  function openAdvancedSearch(e, data) {
    e.preventDefault();

    window.open("/settlement/advanced?" + data[0] + "=" + data[1]);
  }

  function openInNewWindow(e, uri) {
    e.preventDefault();
    let url = uri;
    // console.log(window.URL);
    window.open(url.toString(), "_blank", "noreferrer");
  }

  function formatDate(value) {
    let date = new Date(value * 1);
    let day = date.getDate() < 10 ? "0" + date.getDate() : date.getDate();
    let month = date.getMonth() + 1 < 10 ? "0" + (date.getMonth() + 1) : date.getMonth() + 1;
    let hours = date.getHours() < 10 ? "0" + date.getHours() : date.getHours();
    let minutes = date.getMinutes() < 10 ? "0" + date.getMinutes() : date.getMinutes();

    let dateformatted = day + "/" + month + "/" + date.getFullYear() + " " + hours + ":" + minutes;
    let timezone = (date.getTimezoneOffset() * -1) / 60;
    timezone = timezone > 0 ? "+" + timezone : timezone;
    return dateformatted;
  }

  function formatStatusTime(fixture, status) {
    let minFinished = null;
    let hours = "";
    let minutes = "";
    let timestamp = status === "In progress" ? fixture.start_date_utc : fixture.finished_at_utc;
    if (timestamp) {
      let now = new Date();
      let finished_at = new Date(timestamp * 1);
      minFinished = now.getTime() - finished_at.getTime();
      minFinished = Math.round(minFinished / 1000 / 60);
      hours = Math.floor(minFinished / 60);
      minutes = minFinished - hours * 60;
      hours = hours < 10 ? "0" + hours : hours;
      minutes = minutes < 10 ? "0" + minutes : minutes;
    }

    return hours + ":" + minutes;
  }

  const handleOpenActionsMenu = (event) => {
    event.preventDefault();
    setOpenActionsMenu(event.currentTarget);
  };

  const handleCloseActionsMenu = () => {
    setOpenActionsMenu(false);
  };

  // Render the notifications menu
  const renderActionsMenu = () => (
    <Menu
      anchorEl={openActionsMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openActionsMenu)}
      onClose={handleCloseActionsMenu}
      sx={{ mt: 2 }}
    >
      <SoftBox>
        <Grid>
          <SoftBox>
            <Button
              className="MuiButton-outlined-info"
              variant="outlined"
              color="info"
              size="small"
              onClick={(e) => postponeEvent(e)}
              title="Posponed Event - Refund All Event Odds"
            >
              <Icon
                title="Posponed Event - Refund All Event Odds"
                className="info-icon-button"
                style={{ cursor: "pointer" }}
              >
                event_busy_icon
              </Icon>
              Postponed
            </Button>
          </SoftBox>
          <SoftBox>
            <Button
              className="MuiButton-outlined-error"
              variant="outlined"
              color="error"
              size="small"
              onClick={(e) => refundAllUnsettledOdds(e)}
              title="Refund All Bet-Odds"
            >
              <Icon
                title="Refund All Bet-Odds"
                className="error-icon-button"
                style={{ cursor: "pointer" }}
              >
                currency_exchange_icon
              </Icon>
              Refund All
            </Button>
          </SoftBox>
          <SoftBox>
            <Button
              className="MuiButton-outlined-warning"
              variant="outlined"
              color="warning"
              size="small"
              onClick={(e) => unsettledAllOdds(e)}
              title="Unsettle All Bet-Odds"
            >
              <Icon
                title="Unsettle All Bet-Odds"
                className="warning-icon-button"
                style={{ cursor: "pointer" }}
              >
                money_off_icon
              </Icon>
              UnSettle All
            </Button>
          </SoftBox>
        </Grid>
      </SoftBox>
    </Menu>
  );

  useEffect(() => {
    setOpen(showModal);
  }, [showModal]);

  useEffect(() => {
    if (data) {
      //console.log(data);
      settlements = [];
      setUpdateEnabled(false);
      setSelectedFixture(data);
      fixtureData = data;
      fetchSettlementValues();
      fetchOdds(data.ids, data.sport);
    }
  }, [data]);

  useEffect(() => {
    if (providers) {
      //setAvailableProviders(providers);
      availableProviders = providers;
    }
  }, [providers]);

  useEffect(() => {
    return () => {
      setDataTableData({ columns: [], rows: [] });
      settlements = [];
      fixtureData = null;
      setSelectedFixture(null);
    };
  }, []);

  const selectedFixtureProviders = Array.isArray(selectedFixture?.providers)
    ? selectedFixture.providers
    : [];
  const selectedFixtureEventIds = Array.isArray(selectedFixture?.eventIds)
    ? selectedFixture.eventIds
    : [];
  const vbetProviderIndex = selectedFixtureProviders.findIndex((item) => item === "vbet");
  const oneXbetProviderIndex = selectedFixtureProviders.findIndex((item) => item === "1xbet");

  return (
    <div>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        key={"topright"}
        open={notification}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={`${apiResponse}`}
          sx={{ width: "100%" }}
          className={`api-${apiResponse}-notifications`}
        >
          {apiResponseMsg}
        </Alert>
      </Snackbar>
      <Dialog
        open={open}
        onClose={handleCancelEdit}
        fullWidth={true}
        maxWidth={noteExist ? "xxxl" : "xxxl"}
        // sx={{ maxWidth: "calc(100vw) !important" }}
      >
        <DialogTitle>
          <Grid container spacing={3}>
            <Grid item xs={4} style={{ textTransform: "capitalize" }}>
              Edit Odds Settlement for:
            </Grid>
            <Grid item xs={8} pb={1} style={{ display: "flex" }}>
              <SoftBox style={{ marginLeft: "auto" }}>
                <Button
                  className="efbet-button"
                  style={{
                    display: selectedFixture
                      ? vbetProviderIndex > -1
                        ? ""
                        : "none"
                      : "none",
                  }}
                  disabled={!selectedFixture}
                  onClick={(e) =>
                    searchEfBet(
                      e,
                      selectedFixture
                        ? selectedFixtureEventIds[vbetProviderIndex]
                        : null
                    )
                  }
                >
                  Efbet Results
                </Button>
              </SoftBox>
              <SoftBox>
                <Button
                  className="vbet-button"
                  style={{
                    display: selectedFixture
                      ? vbetProviderIndex > -1
                        ? ""
                        : "none"
                      : "none",
                  }}
                  disabled={!selectedFixture}
                  onClick={(e) =>
                    searchVbet(
                      e,
                      selectedFixture
                        ? selectedFixtureEventIds[vbetProviderIndex]
                        : null
                    )
                  }
                >
                  Vbet Results
                </Button>
              </SoftBox>
              <SoftBox>
                <Button
                  className="betfoot-button"
                  style={{
                    display: selectedFixture
                      ? oneXbetProviderIndex > -1
                        ? ""
                        : "none"
                      : "none",
                  }}
                  disabled={!selectedFixture}
                  onClick={(e) =>
                    searchBetfoot(
                      e,
                      selectedFixture
                        ? selectedFixtureEventIds[oneXbetProviderIndex]
                        : null,
                      selectedFixture ? selectedFixture.sport : null
                    )
                  }
                >
                  1xbet Results
                </Button>
              </SoftBox>
              <SoftBox>
                <Button
                  className="sofa-button"
                  disabled={!selectedFixture}
                  onClick={(e) => searchSofa(e, selectedFixture)}
                >
                  Search SofaScore
                </Button>
              </SoftBox>
              <SoftBox>
                <Button
                  disabled={!selectedFixture}
                  onClick={(e) =>
                    searchGoogle(
                      e,
                      selectedFixture ? selectedFixture.home + " vs " + selectedFixture.away : null
                    )
                  }
                >
                  search web
                </Button>
              </SoftBox>
              <SoftBox>
                <Button
                  color="warning"
                  disabled={!selectedFixture}
                  onClick={(e) =>
                    searchBetsApi(
                      e,
                      selectedFixture ? selectedFixture.home + " vs " + selectedFixture.away : null
                    )
                  }
                >
                  search betsApi
                </Button>
              </SoftBox>
            </Grid>
            <Grid
              flexDirection="row"
              item
              xs={12}
              pb={1}
              style={{
                textAlign: "right",
                paddingRight: "15px",
                paddingTop: "0px",
                display: "flex",
              }}
            >
              <SoftBox pr={1} style={{ marginRight: "auto" }}>
                <SoftTypography variant="text" style={{ color: "#cb0c9f" }}>
                  {selectedFixture ? selectedFixture.sport : ""}
                </SoftTypography>
              </SoftBox>
              <SoftBox pr={2}>
                <SoftTypography
                  variant="caption"
                  style={{
                    color: "rgb(168, 168, 168)",
                    lineHeight: 2.5,
                    display: "flex",
                    textAlign: "left",
                  }}
                >
                  <Icon
                    className={
                      selectedFixture && selectedFixture.blocked
                        ? "success-icon-button"
                        : "error-icon-button"
                    }
                    style={{ cursor: "pointer" }}
                    onClick={(e) => handleBlockFixture(e, selectedFixture)}
                    title={"Block Fixture from Feed"}
                  >
                    {selectedFixture && selectedFixture.blocked
                      ? "play_circle_outline_icon"
                      : "do_disturb_icon"}
                  </Icon>
                </SoftTypography>
              </SoftBox>
              <SoftBox pr={2}>
                <SoftTypography
                  variant="caption"
                  style={{
                    color: "rgb(168, 168, 168)",
                    lineHeight: 2.5,
                    display: "flex",
                    textAlign: "left",
                  }}
                >
                  <Icon
                    className="info-icon"
                    style={{ cursor: "pointer" }}
                    onClick={(e) => handleUUIDCopy(e, selectedFixture.fixture_id)}
                  >
                    copy
                  </Icon>
                  <Button
                    disabled={!selectedFixture}
                    onClick={(e) =>
                      openAdvancedSearch(
                        e,
                        selectedFixture
                          ? selectedFixture.matched
                            ? ["uni_id", selectedFixture.fixture_id]
                            : ["provider_id", selectedFixture.fixture_id]
                          : null
                      )
                    }
                  >
                    {selectedFixture ? selectedFixture.fixture_id : ""}
                  </Button>
                </SoftTypography>
              </SoftBox>
              <SoftBox pr={1}>
                <SoftTypography
                  variant="caption"
                  style={{ lineHeight: 2.5, display: "flex", textAlign: "left" }}
                  className={
                    selectedFixture ? selectedFixture.status.replace(" ", "_").toLowerCase() : ""
                  }
                >
                  {selectedFixture ? selectedFixture.status : ""}
                </SoftTypography>
              </SoftBox>
              <SoftBox pr={2}>
                <SoftTypography
                  variant="caption"
                  style={{ lineHeight: 2.5, display: "flex", textAlign: "left" }}
                  className={
                    selectedFixture ? selectedFixture.status.replace(" ", "_").toLowerCase() : ""
                  }
                >
                  {selectedFixture
                    ? "Since: " + formatStatusTime(selectedFixture, selectedFixture.status)
                    : ""}
                </SoftTypography>
              </SoftBox>
              <SoftBox pr={1}>
                <SoftTypography
                  variant="caption"
                  style={{ lineHeight: 2.5, display: "flex", textAlign: "left" }}
                >
                  {selectedFixture ? selectedFixture.region : ""}
                </SoftTypography>
              </SoftBox>
              <SoftBox pr={2}>
                <SoftTypography
                  variant="caption"
                  style={{ color: "info", lineHeight: 2.5, display: "flex", textAlign: "left" }}
                >
                  {selectedFixture ? selectedFixture.competition : ""}
                </SoftTypography>
              </SoftBox>
              <SoftBox pr={1}>
                <SoftTypography
                  variant="caption"
                  style={{ color: "#cb0c9f", lineHeight: 2.5, display: "flex", textAlign: "right" }}
                >
                  {selectedFixture ? selectedFixture.home : ""}
                </SoftTypography>
              </SoftBox>
              <SoftBox pr={1}>
                <SoftTypography
                  variant="caption"
                  style={{
                    color: "rgb(113, 113, 113)",
                    fontSize: "1em",
                    lineHeight: 1.5,
                    display: "flex",
                    textAlign: "center",
                  }}
                >
                  Vs
                </SoftTypography>
              </SoftBox>
              <SoftBox pr={2}>
                <SoftTypography
                  variant="caption"
                  style={{ color: "#cb0c9f", lineHeight: 2.5, display: "flex", textAlign: "left" }}
                >
                  {selectedFixture ? selectedFixture.away : ""}
                </SoftTypography>
              </SoftBox>
              <SoftBox pr={1}>
                <SoftTypography
                  variant="caption"
                  style={{ lineHeight: 2.5, display: "flex", textAlign: "left" }}
                  className={
                    selectedFixture ? selectedFixture.status.replace(" ", "_").toLowerCase() : ""
                  }
                >
                  {selectedFixture ? formatDate(selectedFixture.start_date_utc) : ""}
                </SoftTypography>
              </SoftBox>
              <SoftBox sx={{ lineHeight: 1.3 }}>
                <Button
                  size="small"
                  sx={{
                    padding: "0px 6px !important",
                    lineHeight: "1",
                    height: "20px",
                    minHeight: "10px",
                    backgroundColor: "#f8f9fa !important",
                    borderRadius: "4px !important",
                    border: "1px solid #cecece !important",
                    margin: "0px 0px 0px 15px !important",
                    color: "#ff005490 !important",
                  }}
                  // variant="outlined"
                  // color="warning"
                  onClick={handleOpenActionsMenu}
                >
                  <Icon>electric_bolt_icon</Icon>
                  {" Actions "}
                </Button>
                {renderActionsMenu()}
              </SoftBox>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} alignItems="center">
            <DataTable
              bottomPadding={26}
              table={dataTableData}
              className="modal"
              // headerClassName="fixed-table-header"
              // bodyClassName="settlement-fixtures-list"
              hiddenColumns={hiddenColumns}
              entriesPerPage={{
                defaultValue: 10,
                entries: [5, 10, 15, 20, 25],
              }}
              autoResetPageDefault={false}
              autoResetGlobalFilter={true}
              autoResetSortBy={false}
              canSearch
              tableName="live-settlement"
              tableClassName="live-settlement-table"
              openBackDrop={loading}
            ></DataTable>
          </Grid>
        </DialogContent>
        <DialogActions>
          <SoftButton variant="outlined" color="info" size="small" onClick={handleCancelEdit}>
            Cancel
          </SoftButton>
          <SoftButton
            variant="gradient"
            size="small"
            color={updateEnabled > 0 ? "dark" : "light"}
            disabled={updateEnabled > 0 ? false : true}
            onClick={handleUpdate}
          >
            Update
          </SoftButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// Setting default values for the props of CreatePlatform
EditSettlement.defaultProps = {
  showModal: true,
  signalCloseModal: () => {
    //console.log("close Modal");
  },
};

// Typechecking props of the CreatePlatform
EditSettlement.propTypes = {
  showModal: PropTypes.bool,
  signalCloseModal: PropTypes.func,
  data: PropTypes.object,
};

export default EditSettlement;
